import {
  BloodPressureEntry,
  BodyMeasurementEntry,
  HealthRiskSnapshot,
  LabResultEntry,
  WeightEntry,
} from '../types';

export const SEED_DATE = '2025-08-01';
export const CURRENT_SEED_VERSION = 1;

const id = (prefix: string, suffix: string) => `${prefix}-seed-${suffix}`;

export const seededWeight = (): WeightEntry => ({
  id: id('w', SEED_DATE),
  date: SEED_DATE,
  weight: 142.2,
  height: 168.5,
  bodyFatPercentage: 44.1,
  bmi: 50.0,
  notes: 'HCA GP screening baseline',
});

export const seededBodyMeasurement = (): BodyMeasurementEntry => ({
  id: id('bm', SEED_DATE),
  date: SEED_DATE,
  measurements: { waist: Math.round(0.86 * 168.5 * 10) / 10 },
  notes: 'Derived from waist/height 0.86 (HCA screening)',
});

export const seededBloodPressure = (): BloodPressureEntry => ({
  id: id('bp', SEED_DATE),
  date: SEED_DATE,
  systolic: 153,
  diastolic: 85,
  reading: 1,
  timeOfDay: 'afternoon',
  notes: 'HCA screening single reading',
});

export const seededRiskSnapshot = (): HealthRiskSnapshot => ({
  id: id('risk', SEED_DATE),
  date: SEED_DATE,
  qDiabetes: 30.1,
  qRisk3: 2.6,
  source: 'gp_screening',
  notes: 'HCA screening',
});

type LabSeed = Omit<LabResultEntry, 'id' | 'date' | 'source'>;

const labSeeds: LabSeed[] = [
  // Haematology (FBC)
  { panel: 'fbc', marker: 'haemoglobin',  value: 150,   unit: 'g/L',      referenceLow: 130, referenceHigh: 170, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'haematocrit',  value: 0.467, unit: 'L/L',      referenceLow: 0.400, referenceHigh: 0.500, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'rbc',          value: 5.2,   unit: 'x10e12/L', referenceLow: 4.5, referenceHigh: 5.5, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'mcv',          value: 90.3,  unit: 'fL',       referenceLow: 81,  referenceHigh: 101, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'mch',          value: 29.0,  unit: 'pg',       referenceLow: 27,  referenceHigh: 32,  flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'mchc',         value: 321,   unit: 'g/L',      referenceLow: 315, referenceHigh: 345, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'rdw',          value: 13.4,  unit: '%',        referenceLow: 11.6, referenceHigh: 14.0, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'platelets',    value: 375,   unit: 'x10e9/L',  referenceLow: 150, referenceHigh: 410, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'mpv',          value: 10.8,  unit: 'fL',       referenceLow: 9.0, referenceHigh: 12.0, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'wbc',          value: 9.4,   unit: 'x10e9/L',  referenceLow: 4.0, referenceHigh: 10.0, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'neutrophils',  value: 6.3,   unit: 'x10e9/L',  referenceLow: 2.0, referenceHigh: 7.0, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'lymphocytes',  value: 2.1,   unit: 'x10e9/L',  referenceLow: 1.0, referenceHigh: 3.0, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'monocytes',    value: 0.8,   unit: 'x10e9/L',  referenceLow: 0.2, referenceHigh: 1.0, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'eosinophils',  value: 0.17,  unit: 'x10e9/L',  referenceLow: 0.02, referenceHigh: 0.5, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'basophils',    value: 0.07,  unit: 'x10e9/L',  referenceLow: 0.02, referenceHigh: 0.10, flag: 'normal', cadence: 'annual' },
  { panel: 'fbc', marker: 'esr',          value: 7,     unit: 'mm/Hour',  referenceLow: 0, referenceHigh: 10, flag: 'normal', cadence: 'annual' },

  // Kidney
  { panel: 'kidney', marker: 'sodium',     value: 138, unit: 'mmol/L', referenceLow: 133, referenceHigh: 146, flag: 'normal', cadence: 'annual' },
  { panel: 'kidney', marker: 'potassium',  value: 4.1, unit: 'mmol/L', referenceLow: 3.5, referenceHigh: 5.3, flag: 'normal', cadence: 'annual' },
  { panel: 'kidney', marker: 'urea',       value: 4.1, unit: 'mmol/L', referenceLow: 2.5, referenceHigh: 7.8, flag: 'normal', cadence: 'annual' },
  { panel: 'kidney', marker: 'creatinine', value: 69,  unit: 'umol/L', referenceLow: 62, referenceHigh: 106, flag: 'normal', cadence: 'annual' },
  { panel: 'kidney', marker: 'egfr',       value: 90,  unit: 'mL/min/1.73m2', flag: 'normal', cadence: 'annual', notes: 'Reported as >90' },

  // Diabetes
  { panel: 'hba1c', marker: 'glucose_non_fasting', value: 5.0, unit: 'mmol/L', cadence: 'annual' },
  { panel: 'hba1c', marker: 'hba1c',              value: 41,  unit: 'mmol/mol', referenceLow: 20, referenceHigh: 41, flag: 'normal', cadence: 'annual' },

  // Bone / liver / gout
  { panel: 'liver',    marker: 'calcium',          value: 2.48, unit: 'mmol/L', referenceLow: 2.10, referenceHigh: 2.55, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'calcium_corrected', value: 2.46, unit: 'mmol/L', referenceLow: 2.10, referenceHigh: 2.55, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'inorganic_phosphate', value: 0.94, unit: 'mmol/L', referenceLow: 0.80, referenceHigh: 1.50, flag: 'normal', cadence: 'annual' },
  { panel: 'uric_acid', marker: 'uric_acid',       value: 473, unit: 'umol/L', referenceLow: 200, referenceHigh: 430, flag: 'H', cadence: 'annual' },
  { panel: 'liver',    marker: 'total_protein',    value: 74, unit: 'g/L', referenceLow: 60, referenceHigh: 80, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'albumin',          value: 44, unit: 'g/L', referenceLow: 35, referenceHigh: 50, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'globulin',         value: 30, unit: 'g/L', referenceLow: 20, referenceHigh: 40, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'total_bilirubin',  value: 4,  unit: 'umol/L', referenceHigh: 21, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'alkaline_phosphatase', value: 101, unit: 'IU/L', referenceLow: 30, referenceHigh: 130, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'ast',              value: 26, unit: 'IU/L', referenceHigh: 41, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'alt',              value: 31, unit: 'IU/L', referenceHigh: 41, flag: 'normal', cadence: 'annual' },
  { panel: 'liver',    marker: 'gamma_gt',         value: 31, unit: 'IU/L', referenceLow: 8, referenceHigh: 61, flag: 'normal', cadence: 'annual' },
  { panel: 'other',    marker: 'serum_iron',       value: 10.6, unit: 'umol/L', referenceLow: 5.8, referenceHigh: 34.5, flag: 'normal', cadence: 'annual' },

  // Lipids
  { panel: 'lipids', marker: 'cholesterol_total', value: 6.9,  unit: 'mmol/L', referenceHigh: 5.0, flag: 'H', cadence: 'annual' },
  { panel: 'lipids', marker: 'triglycerides',     value: 5.05, unit: 'mmol/L', referenceHigh: 1.70, flag: 'H', cadence: 'annual' },
  { panel: 'lipids', marker: 'hdl',               value: 1.2,  unit: 'mmol/L', referenceLow: 0.9, referenceHigh: 1.5, flag: 'normal', cadence: 'annual' },
  { panel: 'lipids', marker: 'hdl_chol_ratio',    value: 0.17, unit: 'ratio', referenceLow: 0.25, flag: 'L', cadence: 'annual' },
  { panel: 'lipids', marker: 'chol_hdl_ratio',    value: 5.75, unit: 'ratio', cadence: 'annual' },
  { panel: 'lipids', marker: 'non_hdl',           value: 5.7,  unit: 'mmol/L', cadence: 'annual' },

  // Vitamin D
  { panel: 'vitamin_d', marker: 'vit_d_25oh', value: 26, unit: 'nmol/L', referenceLow: 50, flag: 'L', cadence: 'annual', notes: 'Insufficient (25–50). GP advised 1000–2000 IU/day supplement.' },

  // Thyroid
  { panel: 'thyroid', marker: 'tsh', value: 2.37, unit: 'mU/L', referenceLow: 0.27, referenceHigh: 4.20, flag: 'normal', cadence: 'annual' },
];

export const seededLabResults = (): LabResultEntry[] =>
  labSeeds.map((seed, i) => ({
    id: id('lab', `${SEED_DATE}-${i}`),
    date: SEED_DATE,
    source: 'gp_screening',
    ...seed,
  }));
